"use client";

import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { CartIcon } from "@/components/shop/CartIcon";
function NavItems({
  isMobile,
  onItemClick
}: {
  isMobile?: boolean;
  onItemClick?: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="224951" className={`flex ${isMobile ? "flex-col w-full" : "flex-row"} gap-6 p-4`}>
        {[1, 2, 3, 4].map((_, i) => <div key={i} data-index={i} className="h-5 w-20 bg-muted/50 animate-pulse rounded-sm" />)}
      </div>;
  }
  return <NavigationMenuList className={isMobile ? "flex-col items-start w-full gap-0" : "gap-8"}>
      {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i} className={isMobile ? "w-full" : ""}>
          <NavigationMenuLink asChild>
            <Link to={item.href || "#"} className={`group flex items-center gap-3 text-foreground hover:text-primary transition-colors outline-none ${isMobile ? "w-full py-4 border-b border-border/40" : "py-2"}`} onClick={onItemClick}>
              <span className="font-mono text-[10px] font-medium tracking-widest text-muted-foreground group-hover:text-primary/70 transition-colors">
                {String(i + 1).padStart(2, "0")}
              </span>
              <span className="font-default font-semibold text-sm tracking-wide uppercase">
                {item.label}
              </span>
            </Link>
          </NavigationMenuLink>
        </NavigationMenuItem>)}
    </NavigationMenuList>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section id="header" className={`sticky top-0 z-50 w-full transition-all duration-500 bg-background text-foreground ${isScrolled ? "bg-background/80 backdrop-blur-md border-b border-border/50 shadow-sm" : "bg-background border-b border-transparent"}`}>
      <MenuProvider menu_id="52">
        <div className="container mx-auto px-4 md:px-6 h-20 flex items-center justify-between relative">
          {}
          <div className="flex-shrink-0 flex items-center">
            <Link to="/" className="outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-sm">
              <span className="font-serif text-3xl font-bold tracking-tight text-foreground">
                CRUST
              </span>
            </Link>
          </div>

          {}
          <div className="hidden lg:flex flex-1 justify-center">
            <NavigationMenu viewport={false}>
              <NavItems />
            </NavigationMenu>
          </div>

          {}
          <div className="flex items-center gap-4 flex-shrink-0">
            <CartIcon variant="ghost" size="icon" className="text-foreground hover:text-primary transition-colors" />
            
            <Button variant="default" className="hidden md:inline-flex font-default font-bold uppercase tracking-widest text-[10px] px-6 h-10 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 transition-all hover:scale-105">
              Order Now
            </Button>
            
            <Button variant="ghost" size="icon" className="lg:hidden text-foreground hover:text-primary" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} aria-expanded={mobileMenuOpen} aria-label="Toggle navigation menu">
              {mobileMenuOpen ? <X className="size-6" /> : <Menu className="size-6" />}
            </Button>
          </div>
        </div>

        {}
        {mobileMenuOpen && <div className="lg:hidden absolute top-full left-0 w-full bg-background/98 backdrop-blur-xl border-b border-border shadow-2xl animate-in slide-in-from-top-2 fade-in duration-300">
            <div className="container mx-auto px-4 py-8 flex flex-col gap-8">
              <NavigationMenu viewport={false} className="w-full max-w-full items-start justify-start">
                <NavItems isMobile onItemClick={() => setMobileMenuOpen(false)} />
              </NavigationMenu>
              
              <Button variant="default" className="w-full font-default font-bold uppercase tracking-widest text-[10px] h-12 rounded-full bg-primary text-primary-foreground" onClick={() => setMobileMenuOpen(false)}>
                Order Now
              </Button>
            </div>
          </div>}
      </MenuProvider>
    </section>;
}